import {
  useColorModeValue,
  useDisclosure,
} from '@chakra-ui/react'
import { useCallback, useRef, useState } from 'react'
import { FiCheckCircle, FiDollarSign, FiEye, FiPlay, FiRotateCcw } from 'react-icons/fi'
import { useTranslation } from 'react-i18next'
import { DataTable } from '@/components/common/DataTable'
import type { ActionButton, ColumnConfig } from '@/components/common/DataTable/types'
import { MetaRefundModal } from '@/components/contractManagement/MetaRefundModal'
import DeploymentDetailModal from './DeploymentDetailModal'
import DeploymentSettlementDialog from './DeploymentSettlementDialog'
import { useDeploymentTableColumns } from './deploymentTableColumns'
import { useDeploymentTableFiltering } from './useDeploymentTableFiltering'
import { resolveTerminalCleanupPresentation } from './terminalCleanupPresentation'
import { useToast } from '@/hooks/useToast'
import { useNetworkEnvironment } from '@/hooks/useNetworkEnvironment'
import {
  useContractDeploymentRecords,
  useQueueContractVerification,
  useRetryContractInitialization,
  useTriggerSettlement,
} from '@/hooks/useAdminData'
import type { ContractDeploymentRecord } from '@/types'
import { formatChainName } from '@/constants/chains'
import { formatDateTime } from '@/utils/formatUtils'
import { getVerificationStatusColor, getVerificationStatusText } from '@/utils/statusUtils'
import { triggerBrowserDownload } from '@/services/admin/baseService'
import { contractService } from '@/services/admin/contractService'
import { getDeploymentTablePaymentAddress } from '@/utils/adminAddressPresentation'

interface DeploymentTableTabProps {
  pageSize?: number
}

export const DeploymentTableTab = ({ pageSize = 20 }: DeploymentTableTabProps) => {
  const { t } = useTranslation()
  const { currentEnv } = useNetworkEnvironment()
  const { toast, success: showToastSuccess, error: showToastError } = useToast()
  const borderColor = useColorModeValue('gray.200', 'gray.600')
  const tableCellColor = useColorModeValue('gray.700', 'gray.200')
  const secondaryTextColor = useColorModeValue('gray.500', 'gray.400')
  const modalBg = useColorModeValue('gray.50', 'gray.800')
  const cancelRef = useRef<HTMLButtonElement>(null)

  const [selectedDeploymentRecord, setSelectedDeploymentRecord] = useState<ContractDeploymentRecord | null>(null)
  const [isRefreshingDeploymentDetail, setIsRefreshingDeploymentDetail] = useState(false)
  const [metaRefundDeployment, setMetaRefundDeployment] = useState<ContractDeploymentRecord | null>(null)
  const [settlementTarget, setSettlementTarget] = useState<ContractDeploymentRecord | null>(null)
  const [forceSettlement, setForceSettlement] = useState(false)
  const [forceReason, setForceReason] = useState('')

  const deploymentDetailModal = useDisclosure()
  const metaRefundModal = useDisclosure()
  const settlementDialog = useDisclosure()

  const {
    data: allDeploymentData,
    isLoading,
    error,
    refetch: refetchDeployments,
    isFetching,
  } = useContractDeploymentRecords(undefined, { fetchAll: true })
  const triggerSettlementMutation = useTriggerSettlement()
  const verifyMutation = useQueueContractVerification()
  const initializeMutation = useRetryContractInitialization()

  const {
    deploymentFilters,
    deploymentFilterConfigs,
    deploymentPaginationConfig,
    filteredDeploymentData,
    setDeploymentPage,
    handleDeploymentFiltersChange,
    handleDeploymentPageSizeChange,
  } = useDeploymentTableFiltering({
    pageSize,
    data: allDeploymentData?.data,
  })

  const resolveDeploymentChainId = useCallback((record?: ContractDeploymentRecord | null) => {
    if (!record) return ''
    const numeric = record.chain_numeric_id ?? (record as any).chain_numeric_id ?? (record as any).chainNumericId
    if (typeof numeric === 'number' && Number.isFinite(numeric)) {
      return `${numeric}`
    }
    const registryChain = (record as any).registry_chain_id || (record as any).registryChainId
    if (typeof registryChain === 'string' && registryChain.trim().length > 0) {
      return registryChain
    }
    if (record.chain_id) return record.chain_id
    const chainCode = record.chain_code || (record as any).chainCode
    if (chainCode) return chainCode
    return ''
  }, [])

  const getDeploymentStatusColor = useCallback((record: ContractDeploymentRecord) => {
    switch (record.status_id) {
      case 'cds_successful':
      case 'successful':
        return 'green'
      case 'cds_failed':
      case 'failed':
        return 'red'
      case 'cds_reverted':
      case 'reverted':
        return 'orange'
      case 'cds_pending':
      case 'pending':
        return 'blue'
      default:
        return 'gray'
    }
  }, [])

  const getVerificationStatusColorCallback = useCallback((status?: string) => {
    return getVerificationStatusColor(status ?? 'avs_pending')
  }, [])

  const getVerificationStatusTextCallback = useCallback((status?: string) => {
    return getVerificationStatusText(status ?? 'avs_pending')
  }, [])

  const getInitializationStatusColor = useCallback((isInitialized?: boolean) => {
    return isInitialized ? 'green' : 'yellow'
  }, [])

  const getInitializationStatusText = useCallback((isInitialized?: boolean) => {
    return isInitialized ? 'Initialized' : 'Not Initialized'
  }, [])

  const getInitializationStatusDisplay = useCallback((isInitialized?: boolean) => {
    return isInitialized ? 'Initialized' : 'Not\nInitialized'
  }, [])

  const getSettlementAvailability = useCallback((record: ContractDeploymentRecord | null | undefined) => {
    if (!record) {
      return { ready: false, openAt: undefined as Date | undefined }
    }

    const now = Date.now()

    const parseTimestamp = (value?: string | null) => {
      if (!value) {
        return undefined
      }
      const time = new Date(value).getTime()
      return Number.isNaN(time) ? undefined : time
    }

    let openTimestamp = parseTimestamp(record.sweep_open_at)

    if (openTimestamp === undefined) {
      const deployedTimestamp = parseTimestamp(record.deployed_at)
      if (deployedTimestamp !== undefined) {
        openTimestamp = deployedTimestamp + 48 * 60 * 60 * 1000
      }
    }

    if (openTimestamp === undefined) {
      return { ready: false, openAt: undefined as Date | undefined }
    }

    return {
      ready: openTimestamp <= now,
      openAt: new Date(openTimestamp),
    }
  }, [])

  const formatGasCost = useCallback((record: ContractDeploymentRecord) => {
    const gasUsed =
      typeof record.gas_used === 'string' && record.gas_used.trim().length > 0
        ? record.gas_used.trim()
        : null

    const deploymentCostUsd =
      typeof record.deployment_cost_usd === 'number' && Number.isFinite(record.deployment_cost_usd)
        ? record.deployment_cost_usd
        : null

    if (gasUsed && deploymentCostUsd !== null) {
      return `${gasUsed} gas (~$${deploymentCostUsd.toFixed(4)})`
    }
    if (gasUsed) {
      return `${gasUsed} gas`
    }
    if (deploymentCostUsd !== null) {
      return `~$${deploymentCostUsd.toFixed(4)}`
    }
    return '-'
  }, [])

  const getGasCostDisplayParts = useCallback((record: ContractDeploymentRecord) => {
    const gasUsed =
      typeof record.gas_used === 'string' && record.gas_used.trim().length > 0
        ? record.gas_used.trim()
        : null

    const deploymentCostUsd =
      typeof record.deployment_cost_usd === 'number' && Number.isFinite(record.deployment_cost_usd)
        ? record.deployment_cost_usd
        : null

    return {
      gasLine: gasUsed ? `${gasUsed} gas` : null,
      usdLine: deploymentCostUsd !== null ? `(~$${deploymentCostUsd.toFixed(4)})` : null,
    }
  }, [])

  const getDeploymentDisplayAddress = useCallback((record: ContractDeploymentRecord) => {
    return getDeploymentTablePaymentAddress(record) ?? undefined
  }, [])

  const handleExport = useCallback(async () => {
    try {
      const { blob, filename } = await contractService.exportDeploymentCsv({
        search: deploymentFilters.search || '',
        status: deploymentFilters.deploymentStatus || '',
        verification_status: deploymentFilters.deploymentVerificationStatus || '',
      }, currentEnv)
      triggerBrowserDownload(blob, filename)
      showToastSuccess({
        title: t('common.exportCsv'),
        description: t('common.export.downloaded'),
      })
    } catch (error) {
      showToastError({
        title: t('common.exportCsv'),
        description: error instanceof Error ? error.message : t('common.operationFailed'),
      })
    }
  }, [
    currentEnv,
    deploymentFilters.deploymentStatus,
    deploymentFilters.deploymentVerificationStatus,
    deploymentFilters.search,
    showToastError,
    showToastSuccess,
    t,
  ])

  const truncateHash = useCallback((hash: string): string => {
    if (!hash || hash.length <= 10) return hash
    return `${hash.slice(0, 6)}...${hash.slice(-4)}`
  }, [])

  const refreshDeploymentDetail = useCallback(async (
    deploymentId: string,
    refreshList: boolean
  ) => {
    setIsRefreshingDeploymentDetail(true)
    try {
      const response = await contractService.getContractDeploymentRecord(
        deploymentId,
        currentEnv
      )
      if (response?.success && response.data) {
        setSelectedDeploymentRecord(current =>
          current?.id === deploymentId ? response.data ?? current : current
        )
      }
      if (refreshList) {
        await refetchDeployments()
      }
    } catch (error) {
      showToastError({
        title: t('common.error'),
        description: error instanceof Error ? error.message : t('common.unknownError'),
      })
    } finally {
      setIsRefreshingDeploymentDetail(false)
    }
  }, [currentEnv, refetchDeployments, showToastError, t])

  const handleViewDeploymentDetails = useCallback(
    (record: ContractDeploymentRecord) => {
      setSelectedDeploymentRecord(record)
      deploymentDetailModal.onOpen()
      void refreshDeploymentDetail(record.id, false)
    },
    [deploymentDetailModal, refreshDeploymentDetail]
  )

  const handleRefreshDeploymentDetails = useCallback(async () => {
    const selectedId = selectedDeploymentRecord?.id
    if (selectedId) {
      await refreshDeploymentDetail(selectedId, true)
    }
  }, [refreshDeploymentDetail, selectedDeploymentRecord?.id])

  const handleOpenMetaRefund = useCallback(
    (record: ContractDeploymentRecord) => {
      setMetaRefundDeployment(record)
      metaRefundModal.onOpen()
    },
    [metaRefundModal]
  )

  const handleOpenSettlement = useCallback(
    (record: ContractDeploymentRecord) => {
      const availability = getSettlementAvailability(record)

      if (!availability.ready) {
        toast({
          title: t('settlements.execute.unavailableTitle'),
          description: availability.openAt
            ? t('contractRegistry.actions.settlementLocked', { time: formatDateTime(availability.openAt) })
            : t('contractRegistry.actions.settlementLockedUnknown'),
          status: 'warning',
          duration: 6000,
          isClosable: true,
        })
        return
      }

      setForceSettlement(false)
      setForceReason('')
      setSettlementTarget(record)
      settlementDialog.onOpen()
    },
    [getSettlementAvailability, settlementDialog, t, toast]
  )

  const handleCloseSettlement = useCallback(() => {
    setSettlementTarget(null)
    setForceSettlement(false)
    setForceReason('')
    settlementDialog.onClose()
  }, [settlementDialog])

  const handleConfirmSettlement = useCallback(async () => {
    if (!settlementTarget) {
      return
    }

    if (forceSettlement && !forceReason.trim()) {
      toast({
        title: t('settlements.execute.fields.force.reasonRequired'),
        status: 'warning',
      })
      return
    }

    try {
      const response = await triggerSettlementMutation.mutateAsync({
        orderId: settlementTarget.order_id,
        force: forceSettlement,
        forceReason: forceSettlement ? forceReason.trim() : undefined,
      })
      if (response.result?.queued) {
        toast({
          title: t('settlements.execute.success', { count: 1 }),
          status: 'success',
        })
      } else {
        const reasonKey = response.result?.reason
        const description = reasonKey
          ? t(`settlements.execute.reasons.${reasonKey}`, reasonKey)
          : response.error || t('settlements.execute.errorDescription')

        toast({
          title: t('settlements.execute.unavailableTitle'),
          description,
          status: 'warning',
          duration: 8000,
          isClosable: true,
        })
      }

      await refetchDeployments()
    } catch (error: any) {
      toast({
        title: t('settlements.execute.errorTitle'),
        description: error?.message || t('settlements.execute.errorDescription'),
        status: 'error',
      })
    } finally {
      handleCloseSettlement()
    }
  }, [
    forceSettlement,
    forceReason,
    handleCloseSettlement,
    refetchDeployments,
    settlementTarget,
    t,
    toast,
    triggerSettlementMutation,
  ])

  const handleCloseMetaRefund = useCallback(() => {
    setMetaRefundDeployment(null)
    metaRefundModal.onClose()
  }, [metaRefundModal])

  const handleInitializeContract = useCallback(
    async (record: ContractDeploymentRecord) => {
      try {
        const orderBusinessId = record.orderId || record.order_id
        const response = await initializeMutation.mutateAsync({
          orderBusinessId,
          data: {},
        })
        const data = response.data

        if (data?.success) {
          const chainName = formatChainName(resolveDeploymentChainId(record) || '', 'short')

          toast({
            title: t('contractInitialization.single.success.title') || 'Contract Initialization Started',
            description:
              t('contractInitialization.single.success.description', {
                orderId: orderBusinessId,
                chainName: chainName || t('common.unknown'),
              }) ||
              `Contract initialization has been queued for order ${orderBusinessId} on ${chainName || 'unknown chain'}. The process will complete in the background and you can monitor the status in the deployment table.`,
            status: 'success',
            duration: 6000,
            isClosable: true,
          })

          setTimeout(() => refetchDeployments(), 1000)
        } else {
          const chainName = formatChainName(resolveDeploymentChainId(record) || '', 'short')
          toast({
            title: 'Contract Initialization Started',
            description: `Contract initialization has been successfully queued for order ${orderBusinessId} on ${chainName || 'unknown chain'}. The initialization process will run in the background and may take a few minutes to complete. You can monitor the progress in the deployment table.`,
            status: 'success',
            duration: 6000,
            isClosable: true,
          })
          setTimeout(() => refetchDeployments(), 1000)
        }
      } catch (error: any) {
        let errorTitle = t('contractInitialization.single.error.title') || 'Contract Initialization Failed'
        let errorDescription = t('contractInitialization.single.error.description') || 'Failed to initialize contract'
        const errorData = error.response?.data
        const publicErrorMessage =
          errorData?.message ||
          errorData?.error ||
          errorData?.data?.message ||
          errorData?.data?.initializationNotes ||
          errorData?.data?.error

        if (error.response?.status) {
          const status = error.response.status

          switch (status) {
            case 404:
              errorTitle = t('contractInitialization.single.error.notFound.title') || 'Contract Not Found'
              errorDescription =
                publicErrorMessage ||
                t('contractInitialization.single.error.notFound.description') ||
                'The contract was not found'
              break
            case 409:
              errorTitle = t('contractInitialization.single.error.conflict.title') || 'Initialization Conflict'
              errorDescription =
                publicErrorMessage ||
                t('contractInitialization.single.error.conflict.description') ||
                'Contract initialization conflict occurred'
              break
            case 429:
              errorTitle = t('contractInitialization.single.error.rateLimit.title') || 'Rate Limited'
              errorDescription =
                publicErrorMessage ||
                t('contractInitialization.single.error.rateLimit.description') ||
                'Too many requests, please try again later'
              break
            case 500:
            case 502:
            case 503:
              errorTitle = t('contractInitialization.single.error.serverError.title') || 'Server Error'
              errorDescription =
                publicErrorMessage ||
                t('contractInitialization.single.error.serverError.description') ||
                'Server error occurred during initialization'
              break
            default:
              if (publicErrorMessage) {
                errorDescription = publicErrorMessage
              } else if (error.message) {
                errorDescription = error.message
              }
          }
        } else if (error.message) {
          errorDescription = error.message
        }

        toast({
          title: errorTitle,
          description: errorDescription,
          status: 'error',
          duration: 6000,
          isClosable: true,
        })
      }
    },
    [initializeMutation, toast, t, refetchDeployments, resolveDeploymentChainId]
  )

  const handleVerifyContract = useCallback(
    async (record: ContractDeploymentRecord) => {
      try {
        const response = await verifyMutation.mutateAsync(record.id)
        const data = response.data

        if (data?.success) {
          const chainName = formatChainName(resolveDeploymentChainId(record) || '', 'short')
          const contractAddress = record.deployed_contract_address || record.predicted_contract_address

          toast({
            title: t('contractVerification.single.success.titleEnhanced') || 'Contract Verification Queued',
            description:
              t('contractVerification.single.success.descriptionEnhanced', {
                orderId: record.orderId || record.order_id,
                chainName: chainName || t('common.unknown'),
                address: contractAddress ? `${contractAddress.slice(0, 6)}...${contractAddress.slice(-4)}` : t('common.unknown'),
                status: data?.verification_status || 'pending',
              }) ||
              `Contract verification has been queued for order ${record.orderId || record.order_id} on ${chainName || 'unknown chain'}`,
            status: 'success',
            duration: 4000,
            isClosable: true,
          })

          setTimeout(() => refetchDeployments(), 1000)
        } else {
          const chainName = formatChainName(resolveDeploymentChainId(record) || '', 'short')

          toast({
            title: 'Contract Verification Queued',
            description: `Contract verification has been queued for order ${record.orderId || record.order_id} on ${chainName || 'unknown chain'}`,
            status: 'success',
            duration: 4000,
            isClosable: true,
          })

          setTimeout(() => refetchDeployments(), 1000)
        }
      } catch (error: any) {
        let errorTitle = t('contractVerification.single.error.title') || 'Contract Verification Failed'
        let errorDescription = t('contractVerification.single.error.description') || 'Failed to verify contract'

        if (error.response?.status) {
          const status = error.response.status
          const errorData = error.response.data

          switch (status) {
            case 404:
              errorTitle = t('contractVerification.single.error.notFound.title') || 'Contract Not Found'
              errorDescription = t('contractVerification.single.error.notFound.description') || 'The contract was not found'
              break
            case 409:
              errorTitle = t('contractVerification.single.error.conflict.title') || 'Verification Conflict'
              errorDescription =
                t('contractVerification.single.error.conflict.description') || 'Contract verification conflict occurred'
              break
            case 429:
              errorTitle = t('contractVerification.single.error.rateLimit.title') || 'Rate Limited'
              errorDescription =
                t('contractVerification.single.error.rateLimit.description') || 'Too many requests, please try again later'
              break
            case 500:
            case 502:
            case 503:
              errorTitle = t('contractVerification.single.error.serverError.title') || 'Server Error'
              errorDescription =
                t('contractVerification.single.error.serverError.description') || 'Server error occurred during verification'
              break
            default:
              if (errorData?.message) {
                errorDescription = errorData.message
              } else if (error.message) {
                errorDescription = error.message
              }
          }
        } else if (error.message) {
          errorDescription = error.message
        }

        toast({
          title: errorTitle,
          description: errorDescription,
          status: 'error',
          duration: 6000,
          isClosable: true,
        })
      }
    },
    [verifyMutation, toast, t, resolveDeploymentChainId, refetchDeployments]
  )

  const formatSettlementPreviewStatus = useCallback(
    (value?: boolean) => {
      if (value === true) {
        return t('contractRegistry.deployment.table.settlementPreviewStatus.pass')
      }
      if (value === false) {
        return t('contractRegistry.deployment.table.settlementPreviewStatus.fail')
      }
      return t('contractRegistry.deployment.table.settlementPreviewStatus.unknown')
    },
    [t]
  )

  const formatSettlementPreviewDisplay = useCallback(
    (value?: boolean) => {
      const label = formatSettlementPreviewStatus(value)
      return label === 'Not previewed' ? 'Not\npreviewed' : label
    },
    [formatSettlementPreviewStatus]
  )

  const getSettlementPreviewColor = useCallback((value?: boolean) => {
    if (value === true) {
      return 'green'
    }
    if (value === false) {
      return 'red'
    }
    return 'gray'
  }, [])

  const formatTerminalCleanupState = useCallback(
    (record: ContractDeploymentRecord) => {
      const presentation = resolveTerminalCleanupPresentation(record)
      const source = presentation.source
        ? t(`contractRegistry.deployment.table.terminalCleanup.sources.${presentation.source}`, {
          defaultValue: presentation.source,
        })
        : undefined
      return t(
        `contractRegistry.deployment.table.terminalCleanup.${presentation.code}`,
        { source }
      )
    },
    [t]
  )

  const getTerminalCleanupColor = useCallback(
    (record: ContractDeploymentRecord) =>
      resolveTerminalCleanupPresentation(record).colorScheme,
    []
  )

  const handleSettlementTooltip = useCallback(
    (record: ContractDeploymentRecord) => {
      const availability = getSettlementAvailability(record)
      if (availability.ready) {
        return t('contractRegistry.actions.triggerSettlement')
      }
      if (availability.openAt) {
        return t('contractRegistry.actions.settlementLocked', { time: formatDateTime(availability.openAt) })
      }
      return t('contractRegistry.actions.settlementLockedUnknown')
    },
    [getSettlementAvailability, t]
  )

  const splitOrderId = useCallback((id?: string | null) => {
    if (!id) {
      return []
    }
    const trimmed = id.trim()
    if (!trimmed) {
      return []
    }
    if (trimmed.length <= 18) {
      return [trimmed]
    }
    const maxSegments = 3
    const segmentLength = Math.ceil(trimmed.length / maxSegments)
    const segments: string[] = []
    for (let i = 0; i < maxSegments; i += 1) {
      const start = i * segmentLength
      const part = trimmed.slice(start, start + segmentLength)
      if (part) {
        segments.push(part)
      }
    }
    return segments
  }, [])

  const copyToClipboard = useCallback(
    (text: string) => {
      navigator.clipboard.writeText(text)
      toast({
        title: t('common.success'),
        description: t('contractRegistry.actions.addressCopied'),
        status: 'success',
        duration: 2000,
      })
    },
    [t, toast]
  )

  const deploymentColumns: ColumnConfig<ContractDeploymentRecord>[] = useDeploymentTableColumns({
    secondaryTextColor,
    copyToClipboard,
    formatGasCost,
    getGasCostDisplayParts,
    formatSettlementPreviewDisplay,
    formatTerminalCleanupState,
    getDeploymentDisplayAddress,
    getDeploymentStatusColor,
    getInitializationStatusColor,
    getInitializationStatusDisplay,
    getVerificationStatusColor: getVerificationStatusColorCallback,
    getVerificationStatusText: getVerificationStatusTextCallback,
    getSettlementPreviewColor,
    resolveDeploymentChainId,
    splitOrderId,
    truncateHash,
  })

  const getDeploymentRowActions = useCallback(
    (record: ContractDeploymentRecord): ActionButton[] => {
      const actions: ActionButton[] = []

      actions.push({
        label: t('contractRegistry.actions.viewDetails'),
        onClick: () => handleViewDeploymentDetails(record),
        size: 'xs',
        variant: 'ghost',
        icon: <FiEye />,
        tooltip: t('contractRegistry.actions.viewDetails'),
        iconOnly: true,
        ariaLabel: t('contractRegistry.actions.viewDetails'),
      })

      if (
        record.status_id === 'cds_successful' &&
        record.address_verification_status_id === 'avs_verified' &&
        !record.is_initialized
      ) {
        const initializeLabel = t('contractRegistry.actions.initializeContract')
        actions.push({
          label: initializeLabel,
          onClick: () => handleInitializeContract(record),
          size: 'xs',
          variant: 'ghost',
          icon: <FiPlay />,
          tooltip: t('contractInitialization.single.tooltip'),
          loading: initializeMutation.isPending,
          iconOnly: true,
          ariaLabel: initializeLabel,
        })
      }

      if (record.address_verification_status_id === 'avs_pending') {
        actions.push({
          label: t('contractRegistry.actions.verifyContract'),
          onClick: () => handleVerifyContract(record),
          size: 'xs',
          variant: 'ghost',
          icon: <FiCheckCircle />,
          tooltip: t('contractRegistry.actions.verifyContract'),
          loading: verifyMutation.isPending,
          iconOnly: true,
          ariaLabel: t('contractRegistry.actions.verifyContract'),
        })
      }

      const settlementAvailability = getSettlementAvailability(record)
      actions.push({
        label: t('contractRegistry.actions.triggerSettlement'),
        onClick: () => handleOpenSettlement(record),
        size: 'xs',
        variant: 'ghost',
        icon: <FiDollarSign />,
        tooltip: handleSettlementTooltip(record),
        loading: triggerSettlementMutation.isPending && settlementTarget?.order_id === record.order_id,
        disabled: triggerSettlementMutation.isPending || !settlementAvailability.ready,
        iconOnly: true,
        ariaLabel: t('contractRegistry.actions.triggerSettlement'),
      })

      const metaRefundReady =
        Boolean(record.deployed_contract_address) &&
        record.status_id === 'cds_successful' &&
        record.address_verification_status_id === 'avs_verified' &&
        Boolean(record.is_initialized)

      actions.push({
        label: t('contractRegistry.actions.metaRefund'),
        onClick: () => handleOpenMetaRefund(record),
        size: 'xs',
        variant: 'ghost',
        icon: <FiRotateCcw />,
        tooltip: metaRefundReady
          ? t('contractRegistry.actions.metaRefund')
          : t('contractRegistry.actions.metaRefundDisabled'),
        disabled: !metaRefundReady,
        iconOnly: true,
        ariaLabel: t('contractRegistry.actions.metaRefund'),
      })

      return actions
    },
    [
      handleInitializeContract,
      handleOpenMetaRefund,
      handleOpenSettlement,
      handleSettlementTooltip,
      handleVerifyContract,
      handleViewDeploymentDetails,
      getSettlementAvailability,
      initializeMutation.isPending,
      settlementTarget,
      t,
      triggerSettlementMutation.isPending,
      verifyMutation.isPending,
    ]
  )

  return (
    <>
      <DataTable
        data={filteredDeploymentData?.data || []}
        loading={isLoading}
        error={error}
        columns={deploymentColumns}
        filters={deploymentFilterConfigs}
        onFiltersChange={handleDeploymentFiltersChange}
        showFilterBar
        sortable
        pagination={deploymentPaginationConfig}
        onPageChange={setDeploymentPage}
        onPageSizeChange={handleDeploymentPageSizeChange}
        actions={getDeploymentRowActions}
        actionsColumnWidth="6%"
        size="sm"
        emptyMessage={t('contractRegistry.deployment.noRecordsFound')}
        showRefresh
        onRefresh={refetchDeployments}
        refreshing={isFetching}
        showExport
        onExport={handleExport}
        mobileBreakpoint="md"
      />

      <DeploymentDetailModal
        isOpen={deploymentDetailModal.isOpen}
        onClose={deploymentDetailModal.onClose}
        record={selectedDeploymentRecord}
        onRefresh={handleRefreshDeploymentDetails}
        isRefreshing={isRefreshingDeploymentDetail || isFetching}
        borderColor={borderColor}
        secondaryTextColor={secondaryTextColor}
        tableCellColor={tableCellColor}
        modalBg={modalBg}
        resolveDeploymentChainId={resolveDeploymentChainId}
        getDeploymentStatusColor={getDeploymentStatusColor}
        getVerificationStatusColor={statusId =>
          getVerificationStatusColorCallback(statusId ?? undefined)
        }
        getVerificationStatusText={statusId =>
          getVerificationStatusTextCallback(statusId ?? undefined)
        }
        getInitializationStatusColor={initialized =>
          getInitializationStatusColor(initialized ?? undefined)
        }
        getInitializationStatusText={initialized =>
          getInitializationStatusText(initialized ?? undefined)
        }
        getTerminalCleanupColor={getTerminalCleanupColor}
        formatTerminalCleanupState={formatTerminalCleanupState}
        getSettlementPreviewColor={value => getSettlementPreviewColor(value ?? undefined)}
        formatSettlementPreviewStatus={value =>
          formatSettlementPreviewStatus(value ?? undefined)
        }
        formatGasCost={formatGasCost}
        copyToClipboard={copyToClipboard}
      />

      <DeploymentSettlementDialog
        isOpen={settlementDialog.isOpen}
        onClose={handleCloseSettlement}
        onConfirm={handleConfirmSettlement}
        cancelRef={cancelRef}
        settlementTarget={settlementTarget}
        forceSettlement={forceSettlement}
        setForceSettlement={setForceSettlement}
        forceReason={forceReason}
        setForceReason={setForceReason}
        isSubmitting={triggerSettlementMutation.isPending}
      />

      <MetaRefundModal isOpen={metaRefundModal.isOpen} onClose={handleCloseMetaRefund} deployment={metaRefundDeployment} />
    </>
  )
}
